import { BLACK, KING, KNIGHT, PAWN, QUEEN, ROOK, WHITE } from '@chess/core'
import type { Color, PieceType } from '@chess/core'
import { useEffect, useRef, type JSX } from 'react'
import { PieceGlyph } from '../board/pieces.js'
import { hapticsEnabled, hapticsSupported, setHapticsEnabled, vibrate } from '../lib/haptics.js'
import { isMuted, playSound, setMuted } from '../sound/engine.js'
import { applyPrefs, BOARD_THEMES, PIECE_SETS, type Prefs } from './themes.js'

/**
 * A four-by-four corner of a board with a few pieces on it.
 *
 * Sixteen squares is enough to judge a colour scheme and read a piece, and it
 * fits above the fold on a phone next to the controls that change it — which is
 * the entire point of a live preview.
 */
const PREVIEW: readonly (readonly [Color, PieceType] | null)[] = [
  [BLACK, ROOK], [BLACK, KING], null, [BLACK, KNIGHT],
  null, [BLACK, PAWN], [BLACK, PAWN], null,
  null, [WHITE, PAWN], null, [WHITE, PAWN],
  [WHITE, QUEEN], null, [WHITE, KING], [WHITE, ROOK],
]

const PreviewBoard = ({ prefs }: { readonly prefs: Prefs }): JSX.Element => {
  const ref = useRef<HTMLDivElement>(null)

  // Scoped to this element, so scrubbing through themes previews them here
  // without the whole page strobing behind the sheet.
  useEffect(() => {
    if (ref.current !== null) applyPrefs(ref.current, prefs)
  }, [prefs])

  return (
    <div
      ref={ref}
      className="grid aspect-square w-full grid-cols-4 overflow-hidden rounded shadow-lg"
      aria-hidden="true"
    >
      {PREVIEW.map((piece, index) => {
        const light = (Math.floor(index / 4) + index) % 2 === 0
        return (
          <div
            key={index}
            className={`relative ${light ? 'bg-board-light' : 'bg-board-dark'}`}
          >
            {piece !== null && (
              <PieceGlyph color={piece[0]} type={piece[1]} className="absolute inset-0 h-full w-full" />
            )}
          </div>
        )
      })}
    </div>
  )
}

const Toggle = ({
  label,
  hint,
  checked,
  disabled = false,
  onChange,
}: {
  readonly label: string
  readonly hint?: string
  readonly checked: boolean
  readonly disabled?: boolean
  readonly onChange: (value: boolean) => void
}): JSX.Element => (
  <label
    className={`flex items-center justify-between gap-4 rounded bg-panel-raised px-3 py-3 ${
      disabled ? 'opacity-50' : 'cursor-pointer'
    }`}
  >
    <span className="min-w-0">
      <span className="block text-sm font-semibold">{label}</span>
      {hint !== undefined && <span className="mt-0.5 block text-xs text-ink-dim">{hint}</span>}
    </span>
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
      className="h-5 w-5 shrink-0 accent-accent"
    />
  </label>
)

export interface SettingsProps {
  readonly prefs: Prefs
  readonly onChange: (prefs: Prefs) => void
  readonly onClose: () => void
  readonly signedIn: boolean
}

/**
 * A bottom sheet on a phone and a centred dialog on a desktop, which is one
 * flexbox rule rather than two components.
 */
export const Settings = ({ prefs, onChange, onClose, signedIn }: SettingsProps): JSX.Element => {
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center"
      onPointerDown={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Settings"
    >
      <div
        onPointerDown={(event) => event.stopPropagation()}
        className="sheet-in max-h-[90dvh] w-full max-w-md overflow-y-auto rounded-t-panel bg-panel p-5 shadow-2xl safe-bottom sm:rounded-panel"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">Settings</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close settings"
            className="rounded px-2 py-1 text-xl leading-none text-ink-dim hover:text-ink"
          >
            ✕
          </button>
        </div>

        <div className="mt-4 grid grid-cols-[7rem_1fr] gap-4">
          <PreviewBoard prefs={prefs} />
          <p className="self-center text-xs text-ink-dim">
            {signedIn
              ? 'Saved to your account, so it follows you to another device.'
              : 'Saved on this device. Sign in and it follows you everywhere.'}
          </p>
        </div>

        <h3 className="mt-5 text-xs font-semibold tracking-wide text-ink-dim uppercase">Board</h3>
        <div className="mt-2 grid grid-cols-5 gap-2">
          {BOARD_THEMES.map((theme) => (
            <button
              key={theme.id}
              type="button"
              onClick={() => onChange({ ...prefs, board: theme.id })}
              aria-pressed={prefs.board === theme.id}
              className={`overflow-hidden rounded ring-offset-2 ring-offset-panel transition-all ${
                prefs.board === theme.id ? 'ring-2 ring-accent' : 'hover:ring-1 hover:ring-panel-line'
              }`}
            >
              <span className="flex h-9 w-full">
                <span className="h-full w-1/2" style={{ background: theme.light }} />
                <span className="h-full w-1/2" style={{ background: theme.dark }} />
              </span>
              <span className="block py-1 text-[10px] font-semibold text-ink-dim">{theme.label}</span>
            </button>
          ))}
        </div>

        <h3 className="mt-5 text-xs font-semibold tracking-wide text-ink-dim uppercase">Pieces</h3>
        <div className="mt-2 grid grid-cols-3 gap-2">
          {PIECE_SETS.map((set) => (
            <button
              key={set.id}
              type="button"
              onClick={() => onChange({ ...prefs, pieces: set.id })}
              aria-pressed={prefs.pieces === set.id}
              className={`rounded bg-panel-raised px-2 py-2 transition-all ${
                prefs.pieces === set.id ? 'ring-2 ring-accent' : 'hover:bg-panel-line'
              }`}
            >
              <span className="flex justify-center gap-0.5">
                {([KING, KNIGHT] as const).map((type) => (
                  <PieceSwatch key={type} setId={set.id} type={type} />
                ))}
              </span>
              <span className="mt-1 block text-[11px] font-semibold">{set.label}</span>
              <span className="block text-[10px] leading-tight text-ink-dim">{set.note}</span>
            </button>
          ))}
        </div>

        <h3 className="mt-5 text-xs font-semibold tracking-wide text-ink-dim uppercase">Feedback</h3>
        <div className="mt-2 space-y-2">
          <Toggle
            label="Sound"
            checked={!isMuted()}
            onChange={(value) => {
              setMuted(!value)
              if (value) playSound('moveSelf')
            }}
          />
          <Toggle
            label="Vibration"
            {...(hapticsSupported()
              ? {}
              : { hint: 'This browser cannot vibrate — iOS Safari never can' })}
            checked={hapticsEnabled()}
            disabled={!hapticsSupported()}
            onChange={(value) => {
              setHapticsEnabled(value)
              if (value) vibrate('move')
            }}
          />
        </div>
      </div>
    </div>
  )
}

/** One piece rendered in a set that is not the currently applied one. */
const PieceSwatch = ({
  setId,
  type,
}: {
  readonly setId: string
  readonly type: PieceType
}): JSX.Element => {
  const ref = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (ref.current !== null) applyPrefs(ref.current, { board: 'green', pieces: setId })
  }, [setId])

  return (
    <span ref={ref} className="block h-8 w-8">
      <PieceGlyph color={WHITE} type={type} className="h-full w-full" />
    </span>
  )
}
